import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * A labelled segmented single-choice control.
 *
 * Used four times inside the Orders filters panel (ТИП, ЧАСТОТА and the two
 * СОРТИРОВКА controls), which is why it is a shared primitive rather than
 * markup repeated four times in one file.
 *
 * `role="radiogroup"` + `role="radio"` rather than toggle buttons: exactly one
 * option is selected at all times, which is what a radio group means. The
 * status chips above it are the opposite case (many at once) and correctly use
 * `aria-pressed` toggle buttons instead.
 */

export interface SegmentedFieldOption<T extends string> {
  value: T;
  label: string;
  /** Optional decorative icon before the label (aria-hidden; the label names it). */
  icon?: ReactNode;
}

interface Props<T extends string> {
  /** Visible section label; also the group's accessible name. */
  label: string;
  /** Unique id — wires the label to the group via `aria-labelledby`. */
  id: string;
  value: T;
  onChange: (next: T) => void;
  options: SegmentedFieldOption<T>[];
  className?: string;
  /**
   * How the label reads.
   *
   * `"section"` (default) is the uppercase small-caps heading used where the
   * control heads its own group — the filters panel and the schedule block.
   * `"field"` matches the ordinary bold input labels beside it, which is what a
   * segmented control standing in a column OF form fields needs: the create-order
   * mockup draws «Приоритет» exactly like «Обслуживание, мин» above it, and a
   * section heading there reads as a new section starting mid-column.
   */
  labelVariant?: "section" | "field" | "form";
  /**
   * How the group looks (Sprint 218, «Как добавить» in the Team modal).
   *
   * `"solid"` (default, every existing caller): a bordered white group whose
   * selected option is filled with `brand-primary`.
   * `"track"`: a grey track whose selected option is a raised white pill marked
   * by an inset `brand-primary` ring. Used where the modal's own primary button
   * sits right below — a second solid brand fill would compete with it, and a
   * neutral alone (white vs grey) is not allowed to mean "selected" (§10.10).
   */
  variant?: "solid" | "track";
  /** Optional id of a hint that describes the group (e.g. the mode hint). */
  describedBy?: string;
}

export function SegmentedField<T extends string>({
  label,
  id,
  value,
  onChange,
  options,
  className,
  labelVariant = "section",
  variant = "solid",
  describedBy,
}: Props<T>) {
  const track = variant === "track";
  const labelId = `${id}-label`;

  // Arrow keys move the selection, matching the radio-group pattern. Without
  // it a keyboard user can tab INTO the group and then be unable to change the
  // choice, because only the selected radio is a tab stop.
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
    const back = event.key === "ArrowLeft" || event.key === "ArrowUp";
    if (!forward && !back) return;
    event.preventDefault();
    const index = options.findIndex((o) => o.value === value);
    if (index === -1) return;
    const next =
      options[(index + (forward ? 1 : options.length - 1)) % options.length];
    onChange(next.value);
  }

  return (
    <div className={cn("min-w-0", className)}>
      <p
        id={labelId}
        className={cn(
          labelVariant === "form"
            ? // The Team modals' input labels («Имя», «Роль») — the segmented
              // control stands among them and must read as one more field.
              "mb-1.5 text-sm font-semibold text-gray-800"
            : labelVariant === "field"
              ? "mb-1 text-xs font-semibold text-gray-700"
              : "mb-2 text-[11px] font-semibold uppercase tracking-widest text-gray-400",
        )}
      >
        {label}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        onKeyDown={handleKeyDown}
        className={cn(
          track
            ? "flex h-11 items-center gap-1 rounded-xl bg-gray-100 p-1"
            : "flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm",
        )}
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              // Only the selected option is a tab stop (roving tabindex), so
              // the group is ONE stop in the page's tab order rather than three.
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(option.value)}
              className={cn(
                // `whitespace-nowrap` + no `truncate` (owner, 2026-08-22: «чтобы
                // все слова влазили»). A segmented option's whole job is to name
                // the choice, so an ellipsis here — «Постоянны…» — defeats the
                // control. Tighter `px-2` buys the last few px; the label sets
                // the width and the group grows to fit rather than clipping it.
                // `h-10` (40px) — the modal's reference control height, shared with the
                // inputs and the footer buttons beside it. At 36px the segmented
                // control sat a step below everything around it.
                track
                  ? "h-9 flex-1 whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition-all"
                  : "h-10 flex-1 whitespace-nowrap rounded-lg px-2 text-xs font-semibold transition-all",
                track
                  ? selected
                    ? "bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-brand-primary"
                    : "text-gray-500 hover:text-gray-700"
                  : selected
                    ? // BRAND token — runtime `hsl(var(--brand-primary))`, the
                      // dealer's own colour, matching the selected status chip.
                      //
                      // NOT `brand-sidebar`: that token literally means "the colour
                      // of the sidebar" (used only by the shells and the login
                      // split-screen). A dealer sets it to a chrome colour with no
                      // semantic weight — e.g. `#8B7874`, a muddy taupe — so a
                      // segmented control filled with it reads as disabled, not
                      // selected. Nor a fixed `bg-gray-900`, which is a hardcode.
                      "bg-brand-primary text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
              )}
            >
              {option.icon ? (
                <span className="inline-flex items-center justify-center gap-1.5">
                  <span aria-hidden="true" className="inline-flex shrink-0">
                    {option.icon}
                  </span>
                  {option.label}
                </span>
              ) : (
                option.label
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
